'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import type { ComponentType, JSX } from 'react';
import { HeroBackersPill } from '@/components/support-mainnet/hero-backers-pill';
import { HeroCtaRow } from '@/components/support-mainnet/hero-cta-row';
import { HeroJourneyGateBackground } from '@/components/support-mainnet/hero-journey-gate-background';
import { cn } from '@/lib/ui/utils';
const HeroAppMockup: ComponentType<{
  className?: string;
}> = dynamic(() => import('@/components/support-mainnet/hero-mockup/hero-app-mockup').then(module => module.HeroAppMockup), {
  ssr: false,
  loading: () => <HeroAppMockupPlaceholder />
});

/** ARBITRARY: landing launch hero — animation band height before app preview.
 *  Uses `svh` (not `dvh`): the embedded dot-grid canvas cover-fits to this band
 *  reading its own `innerHeight`, so a `dvh` band re-fits + rescales the mark on
 *  every frame as the mobile browser toolbar collapses on scroll (visible
 *  stutter + "the logo scales weirdly" at the top — MOR-707). `svh` is the
 *  stable small-viewport height, so the band — and the canvas — stay fixed
 *  across the toolbar transition. At the top (toolbar shown) `svh` == `dvh`, so
 *  first paint is unchanged. */
const LAUNCH_GATE_BAND_HEIGHT = 'h-[clamp(32rem,calc(68svh+var(--app-header-chrome-height,4rem)),48rem)] md:h-[clamp(36rem,calc(72svh+var(--app-header-chrome-height,4rem)),52rem)]' as const;

/** ARBITRARY: clear the fixed landing header (~80px) plus breathing room. On
 *  short iOS Safari viewports the bottom-aligned copy collapses to the TOP of
 *  this band (a child `h-full` doesn't resolve against the `dvh` band height),
 *  so this top padding — not the band height — is what keeps the hero title
 *  clear of the header (MOR-702). */
const LAUNCH_GATE_TOP_PADDING_CLASS = 'pt-[calc(var(--app-header-chrome-height,5rem)+2rem)]' as const;

/** ARBITRARY: bottom fade over gate animation — 85% of prior 68% / 30rem band */
const LAUNCH_GATE_BOTTOM_FADE_HEIGHT = 'h-[min(57.8%,25.5rem)]' as const;

/** ARBITRARY: no upward lift on mobile so the top padding fully clears the
 *  header; the −4rem lift returns at md+ where the band height resolves and the
 *  copy is genuinely bottom-aligned (MOR-702). */
const LAUNCH_HERO_COPY_TRANSLATE_CLASS = 'translate-y-0 md:-translate-y-16' as const;

/** Break the gate canvas out of `max-w-7xl` so cover-fit shows more artboard width. */
const LAUNCH_GATE_FULL_BLEED_CLASS = 'relative w-screen max-w-[100vw] ml-[calc(50%-50vw)]' as const;
export function HeroLaunchSection() {
  const $ = _c(16);
  const t = useTranslations("supportMainnet.hero");
  let t0;
  if ($[0] !== t) {
    t0 = t("launchTitle");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const heroTitle = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("dark scheme-dark pointer-events-none", LAUNCH_GATE_FULL_BLEED_CLASS, LAUNCH_GATE_BAND_HEIGHT, LAUNCH_GATE_TOP_PADDING_CLASS);
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 top-14 z-0" data-testid="hero-launch-brand-halftone"><HeroJourneyGateBackground /></div>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div aria-hidden="true" className={cn("pointer-events-none absolute inset-x-0 bottom-0 z-[1]", LAUNCH_GATE_BOTTOM_FADE_HEIGHT, "bg-gradient-to-t from-background-secondary from-[22%] via-background-secondary/80 via-[48%] to-transparent")} data-testid="hero-launch-brand-bottom-fade" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("relative mx-auto w-full max-w-3xl", LAUNCH_HERO_COPY_TRANSLATE_CLASS);
    t5 = <div aria-hidden="true" className="pointer-events-none absolute inset-x-[-18%] top-[-35%] bottom-[-10%] z-0 bg-[radial-gradient(ellipse_80%_70%_at_50%_58%,var(--background-secondary)_0%,transparent_72%)]" data-testid="hero-launch-text-radial-fade" />;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== heroTitle) {
    t6 = <h1 id="hero-heading" className="text-balance text-display-lg md:text-display-xl lg:text-[clamp(2.75rem,5vw,4rem)] leading-[100%] tracking-[-0.03em]">{heroTitle}</h1>;
    $[7] = heroTitle;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <HeroBackersPill />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="pointer-events-auto"><HeroCtaRow variant="launch" /></div>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t6) {
    t9 = <div className={t1}>{t2}{t3}<div className="relative z-10 flex h-full flex-col justify-end pointer-events-none px-4 pb-[calc(4rem+0.5rem)] sm:px-6 md:pb-[calc(4rem+1rem)] lg:px-8"><div className={t4}>{t5}<div className="relative z-10 flex flex-col items-center gap-6">{t6}{t7}{t8}</div></div></div></div>;
    $[11] = t6;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <HeroAppMockup className="relative z-10" />;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t9) {
    t11 = <section aria-labelledby="hero-heading" className="relative isolate pb-8 text-center md:pb-12">{t9}{t10}</section>;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  return t11;
}
function HeroAppMockupPlaceholder() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden="true" className="mx-auto h-[728px] w-full max-w-[1200px] rounded-overlay border bg-background-secondary lg:h-[852px]" data-testid="hero-app-mockup-placeholder" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}